import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, IconButton, Switch, Typography } from "@mui/material";
import Loader from "components/Loader";
import LoadingButton from "components/LoadingButton";
import { snack } from "components/toast";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { DialogProps, ResType } from "types";
import { getTitle } from "utils";
import { Reminders } from "data/constants";
import ReactQuill from "lib/react-quill";
import { useEffect, useState } from "react";
import moment from "moment";
import { getUsers } from "api/services/users";
import { usePermissions } from "context/PermissionsProvider";
import { getTasks, getTask } from "api/services/tasks";
import DrawerWrapper from "components/DrawerWrapper";
import { useDebounce } from "hooks/useDebounce";
import FormTime from "components/FormFields/FomTime";
import FormAutoComplete from "components/FormFields/FormAutocomplete";
import FormCheckbox from "components/FormFields/FormCheckbox";
import FormDate from "components/FormFields/FormDate";
import FormInput from "components/FormFields/FormInput copy";
import FormSelect from "components/FormFields/FormSelect";
import { addCalendarEventDefaultValues, AddCalendarEventSchema } from "./addCalendarEvent";
import { createRequestEvent } from "api/services/events";

interface Props extends DialogProps {
    successCb?: () => void;
}

function AddEvent({ open, setOpen }: Props) {
    const queryClient = useQueryClient();
    const { permissions } = usePermissions();

    const [page, setPage] = useState(0);
    const [search, setSearch] = useState("");
    const [taskdata, setTaskdata] = useState<any[]>([]);
    const [clientOptions, setClientOptions] = useState<any[]>([]);
    const [totalCount, setTotalCount] = useState<number | null>(null);
    const [whatsappCheck, setWhatsappCheck] = useState(false);

    const debouncedSearch = useDebounce(search, 400);
    const handleWhatsAppToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        setWhatsappCheck(e.target.checked);
    };
    useEffect(() => {
        if (open) {
            setWhatsappCheck(false);
        }
    }, [open]);

    const { control, watch, handleSubmit, reset } = useForm({
        defaultValues: addCalendarEventDefaultValues,
        mode: "onChange",
        resolver: yupResolver(AddCalendarEventSchema()),
    });

    const { data: users, isLoading: usersLoading }: ResType = useQuery(["users"], getUsers, {
        onError: (err) => {
            if (!err?.error) {
                snack.error("An errror occurred while fetching user-data");
            }
        },
        enabled: open
    });

    const { data: tasks, isLoading: tasksLoading }: ResType = useQuery(
        ["tasks", { client: localStorage.getItem("clientId"), subTasks: true }],
        getTasks,
        {
            onSuccess: (res) => {
                setTaskdata(res?.data?.[0] || []);
            },
            enabled: open,
            onError: (err) => {
                if (!err?.error) {
                    snack.error("An error occurred while fetching task-data");
                }
            },
        }
    );

    const { mutate, isLoading: createLoading } = useMutation(createRequestEvent, {
        onSuccess: () => {
            snack.success(`Task Event Created`);
            setOpen(false);
            reset(addCalendarEventDefaultValues);
            queryClient.invalidateQueries("requested-events");
        },
        onError: (err: any) => {
            if (err && err?.response && err?.response?.data && err?.response?.data?.message) {
                snack.error(err.response.data.message);
            } else if (!err?.error) {
                snack.error("An error occurred while creating Event");
            }
        },
    });

    const onSubmit = (data: any) => {
        const { reminderCheck, ...apiData } = data;
        apiData.clientId = apiData.type === "EVENT" ? null : localStorage.getItem("clientId");
        apiData.task = apiData?.task?.value;
        apiData.reminder = data.reminderCheck ? data.reminder : null;
        apiData.date = moment(apiData.date).toDate();
        mutate({
            ...apiData,
        });
    };

    return (
        <DrawerWrapper open={open} setOpen={setOpen} title="Create an Event Request">
            {tasksLoading || usersLoading ? (
                <Loader />
            ) : (
                <form onSubmit={handleSubmit(onSubmit)}>
                    {watch("type") === "TASK" && (
                        <>
                            <Box mt={2}>
                            </Box>
                            <Box mt={2}>
                                <FormAutoComplete
                                    control={control}
                                    label="Task*"
                                    name="task"
                                    options={taskdata?.map((item: any) => ({
                                        label: `${item?.taskNumber} | ${item?.name}`,

                                        value: item?.id,
                                    })) || []}
                                />
                            </Box>
                            <Box mt={2}>
                            </Box>
                        </>
                    )}
                    <Box mt={2}>
                        <FormInput
                            name="title"
                            control={control}
                            label="Event Title*"
                            inputProps={{ maxLength: 50 }}
                        />
                    </Box>
                    <Box mt={2}>
                        <FormInput
                            name="location"
                            control={control}
                            label="Location*"
                            inputProps={{ maxLength: 50 }}
                        />
                    </Box>
                    <Box mt={2}>
                        <FormDate name="date" control={control} label="Date*" minDate={new Date()} />
                    </Box>
                    <Box mt={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <FormTime name="startTime" control={control} label="Start Time*" />
                            </Grid>
                            <Grid item xs={6}>
                                <FormTime name="endTime" control={control} label="End Time*" />
                            </Grid>
                        </Grid>
                    </Box>
                    {/* <Box mt={2}>
                        <FormCheckbox name="reminderCheck" control={control} label="Set Reminder" />
                    </Box>
                    {watch("reminderCheck") && (
                        <Box mt={2}>
                            <FormSelect
                                name="reminder"
                                control={control}
                                label="Reminder"
                                options={Object.values(Reminders).map((item) => ({
                                    label: getTitle(item),
                                    value: item,
                                }))}
                            />
                        </Box>
                    )} */}
                    <Box mt={2}>
                        <Typography color="primary" variant="h6" sx={{ mb: 1 }}>
                            Event Description
                        </Typography>
                        <Controller
                            control={control}
                            name="notes"
                            render={({ field: { value, onChange } }) => (
                                <ReactQuill
                                    value={value || ""}
                                    onChange={(v: any) => {
                                        onChange(v);
                                    }}
                                    modules={{
                                        clipboard: {
                                            matchVisual: false
                                        }
                                    }}
                                    id="overview"
                                />
                            )}
                        />
                    </Box>
                    <Box mt={3}>
                        <LoadingButton
                            loading={false}
                            disabled={false}
                            type="submit"
                            fullWidth
                            loadingColor="white"
                            title="Create Event"
                            color="secondary"
                        />
                    </Box>
                </form>
            )}
        </DrawerWrapper>
    );
}

export default AddEvent;
